import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  MapPin, Calendar, Users, Heart, CalendarDays, ArrowLeft,
  Building, Theater, Music, Home, Plus, Camera, Crown, TrendingUp, HelpCircle, Lock, Sparkles
} from "lucide-react";
import { type ShowWithDetails } from "@shared/schema";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { usePricingVisibility } from "@/lib/pricing-visibility";
import { useToast } from "@/hooks/use-toast";
import { formatDate, formatCurrency } from "@/modules/calculator";
import { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { normalizeCareerStageDisplay, normalizeCareerStage, formatNumber } from "@/modules/calculator";
import { Link } from "wouter";
import { PackageCard } from "@/components/package-card";
import { type PackageType, PRICING_MULTIPLIERS } from "@/lib/package-config";

interface ShowDetailProps {
  show: ShowWithDetails;
  onAddToProposal?: (show: ShowWithDetails) => void;
  onSaveShow?: (show: ShowWithDetails) => void;
  onBack?: () => void;
  showBackButton?: boolean;
  isAuthenticated?: boolean;
  backToText?: string; // Dynamic text for back button (e.g., "Back to Venue Name" or "Back to Artist Name")
}

export default function ShowDetail({ 
  show, 
  onAddToProposal, 
  onSaveShow, 
  onBack, 
  showBackButton = false,
  isAuthenticated = false,
  backToText
}: ShowDetailProps) {
  const { user } = useAuth();
  const { isPricingVisible } = usePricingVisibility();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [maxEstimate, setMaxEstimate] = useState<{ low: number; high: number } | null>(null);
  const [packagePricing, setPackagePricing] = useState<{ setCore: number; setAudio: number; setVideo: number } | null>(null);
  const [isCalculating, setIsCalculating] = useState(true);

  // Scroll to top when ShowDetail component mounts
  useEffect(() => {
    // Use setTimeout to ensure the content is rendered before scrolling
    const scrollToTop = () => {
      // Look for modal dialog content first, then other scrollable containers
      const dialogContent = document.querySelector('[role="dialog"] .overflow-y-auto');
      const scrollableParent = dialogContent || 
                              document.querySelector('[data-radix-scroll-area-viewport]') || 
                              document.querySelector('.overflow-y-auto') ||
                              window;
      
      if (scrollableParent === window) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      } else if (scrollableParent instanceof HTMLElement) {
        scrollableParent.scrollTo({ top: 0, behavior: 'instant' });
      }
    };

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToTop, 10);
    
    return () => clearTimeout(timeoutId);
  }, [show.id]); // Re-run when show changes

  const defaultImage = "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg";
  const artistImage = show.artist.imageUrl || defaultImage;

  // Use shared UTC-safe date formatter

  const formatPrice = formatCurrency;


  const formatLargeNumber = (num: number) => {
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(1)}B`;
    } else if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toString();
  };



  const getGenreColor = (genre: string) => {
    const colors = {
      "Pop": "bg-pink-100 text-pink-800",
      "Hip-Hop": "bg-indigo-100 text-indigo-800",
      "Rock": "bg-green-100 text-green-800",
      "R&B": "bg-purple-100 text-purple-800",
      "Alternative": "bg-yellow-100 text-yellow-800",
      "Country": "bg-orange-100 text-orange-800",
      "Electronic": "bg-blue-100 text-blue-800",
      "Folk": "bg-emerald-100 text-emerald-800",
      "Reggaeton": "bg-red-100 text-red-800",
      "Latin": "bg-amber-100 text-amber-800",
      "Dance": "bg-cyan-100 text-cyan-800",
      "Singer-Songwriter": "bg-teal-100 text-teal-800",
      "Indie Rock": "bg-slate-100 text-slate-800",
    };
    return colors[genre as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  const getVenueType = (capacity: number | null) => {
    if (!capacity) return "Unknown Venue";
    if (capacity < 500) return "Intimate Venue";
    if (capacity < 2000) return "Club";
    if (capacity < 5000) return "Theater";
    if (capacity < 15000) return "Arena";
    return "Stadium";
  };

  const getVenueTypeIcon = (capacity: number | null) => {
    if (!capacity) return Building;
    if (capacity < 500) return Home;
    if (capacity < 2000) return Music;
    if (capacity < 5000) return Theater;
    if (capacity < 15000) return Building;
    return Building;
  };

  const formatCapacity = (capacity: number | null) => {
    if (!capacity) return 'Unknown';
    return Math.ceil(capacity).toLocaleString();
  };

  // Calculate MAXtimate pricing
  useEffect(() => {
    const calculateMaxtimate = async () => {
      setIsCalculating(true);
      try {
        // Determine market size from DMA population if available
        const getMarketSize = (dmaPopulation?: number | null) => {
          if (!dmaPopulation) return 'Medium';
          if (dmaPopulation >= 2000000) return 'Large';
          if (dmaPopulation >= 700000) return 'Medium';
          return 'Small';
        };

        const marketSize = getMarketSize(show.venue.market?.population);
        const careerStage = normalizeCareerStage(show.artist.careerStage || 'Emerging');
        const venueCapacity = show.venue.capacity || 10000;
        const dmaPopulation = show.venue.market?.population;

        // Call calculator API for No Media pricing only
        const noMediaResponse = await fetch('/api/calculator/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            careerStage,
            numberOfShows: 1,
            mediaObjective: 'No Media',
            marketSize,
            venueCapacity,
            dmaPopulation
          })
        });

        const noMediaData = noMediaResponse.ok ? await noMediaResponse.json() : null;

        // Set MAXtimate pricing and package pricing
        if (noMediaData) {
          const price = noMediaData?.estimatedCost?.base || 0;
          
          setMaxEstimate({
            low: price,
            high: price // Same value for both to maintain the structure
          });
          
          // Use server-calculated package prices if available
          if (noMediaData.packagePricing) {
            setPackagePricing(noMediaData.packagePricing);
          } else {
            // Fallback to old multiplier logic
            setPackagePricing({
              setCore: Math.round(price * PRICING_MULTIPLIERS.SET_CORE),
              setAudio: Math.round(price * PRICING_MULTIPLIERS.SET_AUDIO),
              setVideo: Math.round(price * PRICING_MULTIPLIERS.SET_VIDEO)
            });
          }
        } else {
          // Fallback MAXtimate calculation (No Media equivalent)
          const capacity = venueCapacity;
          const baseRate = 0.5;
          const baseEstimate = capacity * baseRate;
          const basePrice = Math.round(baseEstimate * 0.8); // Use lower end of range for "No Media"

          setMaxEstimate({
            low: basePrice,
            high: basePrice // Same value for both
          });

          setPackagePricing({
            setCore: Math.round(basePrice * PRICING_MULTIPLIERS.SET_CORE),
            setAudio: Math.round(basePrice * PRICING_MULTIPLIERS.SET_AUDIO),
            setVideo: Math.round(basePrice * PRICING_MULTIPLIERS.SET_VIDEO)
          });
        }
      } catch (error) {
        console.error('Error in MAXtimate calculation:', error);
        
        // Error fallback calculation (No Media equivalent)
        const capacity = show.venue.capacity || 10000;
        const baseRate = 0.5;
        const baseEstimate = capacity * baseRate;
        const basePrice = Math.round(baseEstimate * 0.8); // Use lower end for "No Media"

        setMaxEstimate({
          low: basePrice,
          high: basePrice // Same value for both
        });

        setPackagePricing({
          setCore: Math.round(basePrice * PRICING_MULTIPLIERS.SET_CORE),
          setAudio: Math.round(basePrice * PRICING_MULTIPLIERS.SET_AUDIO),
          setVideo: Math.round(basePrice * PRICING_MULTIPLIERS.SET_VIDEO)
        });
      } finally {
        setIsCalculating(false);
      }
    };

    calculateMaxtimate();
  }, [show]);

  return (
    <div className="space-y-6">
      {/* Back button */}
      {showBackButton && onBack && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {backToText || "Back to Shows"}
          </Button>
        </div>
      )}

      {/* Artist and Show Header */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
        <div className="w-20 h-20 flex-shrink-0">
          <img
            src={show.artist.imageUrl || "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg"}
            alt={show.artist.name}
            className="w-full h-full object-cover rounded-lg"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              if (img.src !== "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg") {
                img.src = "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg";
              }
            }}
          />
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Artist Name & Career Stage */}
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-archivo font-bold text-gray-900 dark:text-white">{show.artist.name}</h3>
            {show.artist.careerStage && (
              <div className="flex items-center gap-1">
                <Crown className="h-4 w-4 text-max-primary" />
                <span className="text-max-primary font-medium text-sm">{normalizeCareerStageDisplay(show.artist.careerStage)}</span>
              </div>
            )}
          </div>
          
          {/* Genres */}
          {show.artist.genres && Array.isArray(show.artist.genres) && show.artist.genres.length > 0 && (
            <div className="flex items-center gap-1 mb-3">
              <Music className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground text-sm">
                {show.artist.genres.slice(0, 2).join(', ')}
                {show.artist.genres.length > 2 && ` +${show.artist.genres.length - 2}`}
              </span>
            </div>
          )}
          
          {/* Show Details - Compact horizontal layout */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              <span>{show.venue.name} • {show.venue.city}, {show.venue.state}</span>
            </div>
            <div className="flex items-center gap-1">
              <CalendarDays className="h-4 w-4" />
              <span>{formatDate(show.date)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Building className="h-4 w-4" />
              <span>{getVenueType(show.venue.capacity)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>{formatCapacity(show.venue.capacity)}</span>
            </div>
          </div>
        </div>
      </div>



      {/* MAXtimate Card */}
      <Card className="border-2 border-max-primary/20">
        <CardContent className="space-y-6 pt-6">
          {/* MAXtimate Range */}
          {user && isPricingVisible ? (
            // Authenticated users see full pricing with header
            <div className="bg-max-primary/5 border border-max-primary/20 rounded-lg p-6">
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-4xl font-archivo font-black text-max-primary tracking-tight">
                    MAXtimate™
                  </h2>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="text-max-primary/70 hover:text-max-primary transition-colors">
                        <HelpCircle className="w-5 h-5" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-4" side="top">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-max-primary">What is MAXtimate™?</h4>
                        <p className="text-sm text-muted-foreground">
                          MAXtimate™ is our proprietary pricing model that calculates sponsorship costs based on:
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                          <li>• Artist career stage and popularity</li>
                          <li>• Venue capacity and market size</li>
                          <li>• Campaign objective pricing ranges (No Media to Awareness)</li>
                          <li>• Real-time market data and performance metrics</li>
                        </ul>
                        <p className="text-xs text-muted-foreground italic pt-2">
                          MAXtimate™ provides instant, data-driven sponsorship valuations.
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-archivo font-bold text-green-600 mb-2">
                  {isCalculating ? (
                    "Calculating..."
                  ) : packagePricing ? (
                    `${formatPrice(packagePricing.setCore)} - ${formatPrice(packagePricing.setVideo)}`
                  ) : (
                    "Unable to calculate"
                  )}
                </div>
                <p className="text-xs text-muted-foreground/70 italic">
                  *Volume pricing available: Multi-show proposals qualify for reduced per-show rates
                </p>
              </div>
            </div>
          ) : user && !isPricingVisible ? (
            // Authenticated users with pricing hidden
            <div className="bg-max-primary/5 border border-max-primary/20 rounded-lg p-6">
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-4xl font-archivo font-black text-max-primary tracking-tight">
                    MAXtimate™
                  </h2>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="text-max-primary/70 hover:text-max-primary transition-colors">
                        <HelpCircle className="w-5 h-5" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-4" side="top">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-max-primary">What is MAXtimate™?</h4>
                        <p className="text-sm text-muted-foreground">
                          MAXtimate™ is our proprietary pricing model that calculates sponsorship costs based on:
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                          <li>• Artist career stage and popularity</li>
                          <li>• Venue capacity and market size</li>
                          <li>• Campaign objective pricing ranges (No Media to Awareness)</li>
                          <li>• Real-time market data and performance metrics</li>
                        </ul>
                        <p className="text-xs text-muted-foreground italic pt-2">
                          MAXtimate™ provides instant, data-driven sponsorship valuations.
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="text-center">
                <p className="text-lg font-medium text-gray-700 mb-2">
                  Contact us for custom pricing based on your campaign goals
                </p>
                <p className="text-sm text-muted-foreground">
                  Our team will work with you to create a package that fits your budget and objectives
                </p>
              </div>
            </div>
          ) : (
            // Non-authenticated users see blurred pricing with auth prompt
            <div className="bg-max-primary/5 rounded-lg relative p-4">
              {/* Blurred background content - positioned behind overlay */}
              <div className="filter blur-sm select-none pointer-events-none opacity-30 absolute inset-0">
                <div className="flex flex-col items-center justify-center text-center h-full p-2">
                  <div className="mb-4">
                    <h2 className="text-4xl font-archivo font-black text-max-primary tracking-tight">
                      MAXtimate™
                    </h2>
                  </div>
                  <div>
                    <div className="text-4xl font-archivo font-bold text-green-600 mb-2">
                      $XX,XXX - $XX,XXX
                    </div>
                    <p className="text-xs text-muted-foreground/70 italic">
                      *Volume pricing available: Multi-show proposals qualify for reduced per-show rates
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Glass morphism overlay - expands with content */}
              <div className="backdrop-blur-sm bg-white/30 dark:bg-black/30 rounded-lg border border-white/20 dark:border-white/10 relative z-10">
                <div className="flex flex-col items-center justify-center p-2 text-center w-full max-w-full min-h-[200px]">
                  {/* Lock Icon with gradient background */}
                  <div className="relative mb-3 flex-shrink-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur-lg opacity-50 animate-pulse"></div>
                    <div className="relative bg-gradient-to-r from-blue-500 to-purple-600 p-2.5 rounded-full">
                      <Lock className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  
                  {/* Title with gradient text and tooltip - responsive */}
                  <div className="flex items-center justify-center gap-2 mb-3 px-2">
                    <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent text-center">
                      Unlock MAXtimate™ Pricing
                    </h3>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="text-blue-500/70 hover:text-blue-500 transition-colors flex-shrink-0">
                          <HelpCircle className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-4" side="top">
                        <div className="space-y-2">
                          <h4 className="font-semibold text-max-primary">What is MAXtimate™?</h4>
                          <p className="text-sm text-muted-foreground">
                            MAXtimate™ is our proprietary pricing model that calculates sponsorship costs based on:
                          </p>
                          <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                            <li>• Artist career stage and popularity</li>
                            <li>• Venue capacity and market size</li>
                            <li>• Campaign objective pricing ranges (No Media to Awareness)</li>
                            <li>• Real-time market data and performance metrics</li>
                          </ul>
                          <p className="text-xs text-muted-foreground italic pt-2">
                            MAXtimate™ provides instant, data-driven sponsorship valuations.
                          </p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {/* CTA Button */}
                  <div className="w-full mb-3 px-4">
                    <Link href="/signup">
                      <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white border-0 text-sm py-2.5 px-8 w-full sm:w-auto">
                        Create Free Account
                      </Button>
                    </Link>
                  </div>
                  
                  {/* Description - responsive */}
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 px-2 text-center leading-tight">
                    Create a free account to see detailed pricing and cost breakdowns
                  </p>
                  
                  {/* Benefits at bottom */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Sparkles className="h-3 w-3 flex-shrink-0" />
                    <span>Join 500+ marketers</span>
                  </div>
                  
                  {/* Sign in link moved to subtle position */}
                  <div className="mt-2">
                    <Link href="/login">
                      <Button variant="ghost" size="sm" className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-0 h-auto">
                        Already have an account? Sign in
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sponsorship Details Card */}
      <Card>
        <CardContent className="pt-6">
          {/* Package Tiers */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-max-primary" />
              <h3 className="text-lg font-bold font-archivo text-gray-900 dark:text-gray-100">Sponsorship Packages</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PackageCard
                packageType="set-core"
                price={user && packagePricing ? packagePricing.setCore : 0}
                pricePerShow={user && packagePricing ? packagePricing.setCore : 0}
                selectedShowCount={1}
                onAddToProposal={() => {}} // No-op since this is display only in modal
                disabled={true} // Disable adding from modal
                isCalculating={false}
                showPricing={isPricingVisible}
              />

              <PackageCard
                packageType="set-audio"
                price={user && packagePricing ? packagePricing.setAudio : 0}
                pricePerShow={user && packagePricing ? packagePricing.setAudio : 0}
                selectedShowCount={1}
                onAddToProposal={() => {}} // No-op since this is display only in modal
                disabled={true} // Disable adding from modal
                isCalculating={false}
                showPricing={isPricingVisible}
              />

              <PackageCard
                packageType="set-video"
                price={user && packagePricing ? packagePricing.setVideo : 0}
                pricePerShow={user && packagePricing ? packagePricing.setVideo : 0}
                selectedShowCount={1}
                onAddToProposal={() => {}} // No-op since this is display only in modal
                disabled={true} // Disable adding from modal
                isCalculating={false}
                showPricing={isPricingVisible}
              />
            </div>
            
            {/* Volume Discount Notice */}
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  Volume Discounts Available
                </span>
                <span className="text-sm text-green-700">
                  • 5+ shows: 10% off • 10+ shows: 15% off • 20+ shows: 20% off
                </span>
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Sponsorship Add-ons */}
          <div>
            <h6 className="font-semibold mb-4">Sponsorship Add-ons</h6>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Show Add-ons */}
              <div className="space-y-2">
                <div className="font-medium text-sm text-max-primary flex items-center gap-1">
                  <Plus className="w-4 h-4" />
                  Show Add-ons
                </div>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <div>• Pre-show announcement</div>
                  <div>• Product sampling booth</div>
                  <div>• Exclusive meet & greet</div>
                  <div>• Opening act sponsorship</div>
                </div>
              </div>

              {/* Media Add-ons */}
              <div className="space-y-2">
                <div className="font-medium text-sm text-max-primary flex items-center gap-1">
                  <Camera className="w-4 h-4" />
                  Media Add-ons
                </div>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <div>• Professional photos</div>
                  <div>• Video content rights</div>
                  <div>• Live stream integration</div>
                  <div>• Interview opportunity</div>
                </div>
              </div>

              {/* VIP Perks */}
              <div className="space-y-2">
                <div className="font-medium text-sm text-max-primary flex items-center gap-1">
                  <Crown className="w-4 h-4" />
                  VIP Perks
                </div>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <div>• VIP seating area</div>
                  <div>• Backstage access</div>
                  <div>• Exclusive merchandise</div>
                  <div>• Artist collaboration</div>
                </div>
              </div>
            </div>
          </div>

          {/* Sponsorship Add-ons Contact */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-6">
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                For Sponsorship Add-ons - Contact Us for pricing
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            {user && onAddToProposal && (
              <Button onClick={() => onAddToProposal(show)} className="flex-1 btn-max-primary">
                Add to Proposal
              </Button>
            )}

            {/* REMOVED: Save/Unsave button - saved shows functionality disabled */}

            {!user && (
              <div className="text-sm text-muted-foreground">
                <a href="/login" className="text-primary hover:underline">
                  Sign in
                </a> to create proposals
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
